import { Link } from 'react-router-dom';
import { Menu as MenuIcon } from 'lucide-react';
import type { Me } from '@/api/types';
import { useIdeaFlag } from '@/components/ideas/flags';
import { Button, kitButtonProps, Menu, MenuItem, useDisclosure } from '@/kit';
import { AskLauncherButton } from '@/features/ask';
import type { MenuItemDecl } from '@/features/apps/apps-api';
import { NotificationBell } from './NotificationBell';
import { ShowQaRestore } from './ShowQaToggle';
import { ThemeToggle } from './ThemeToggle';
import { UserMenu } from './UserMenu';


/** The page's name: the navigable entry whose route is the longest prefix of the path. */
export function currentTitle(pathname: string, items: readonly MenuItemDecl[], fallback: string): string {
  const path = (r: string) => r.split(/[?#]/)[0] ?? '';
  const match = items
    .filter((n) => path(n.route).length > 1 && (pathname === path(n.route) || pathname.startsWith(`${path(n.route)}/`)))
    .sort((a, b) => path(b.route).length - path(a.route).length)[0];
  return match?.label ?? fallback;
}

export function TopBar({ me, loading, onAsk, nav, title }: { me?: Me; loading: boolean; onAsk: () => void; nav: readonly MenuItemDecl[]; title: string }) {
  const mentionsOn = useIdeaFlag('mentions');
  // R-14 T-087: shell actions read their tier like any element (`shell.<name>`; no row → Expert)
  const askShown = useDisclosure('shell.ask');
  const bellShown = useDisclosure('shell.notifications');
  return (
    // #727: the signed-in controls sit at the right of the L0 bar, kit buttons on the bar's colours (R-13)
    <>
      <span className="k-l0-menu">
        <Menu
          aria-label="Navigation"
          trigger={
            <Button icon aria-label="Open navigation">
              <MenuIcon aria-hidden />
            </Button>
          }
        >
          {nav.map((item) => (
            <MenuItem key={item.id} render={<Link to={item.route} />}>
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      </span>

      {/* QA11-P1-13, gate B7: one h1 per page — the page head's; the shell names the page for assistive tech without a second h1 */}
      <p className="sr-only">{title}</p>

      {me ? <ShowQaRestore roles={me.roles.map((r) => r.role)} /> : null}
      {/* v1.2 change 01: the tools in order — Ask, notifications, theme, account; below 768 Ask and theme fold into the account menu */}
      {askShown ? (
        <span className="k-wide-only">
          <AskLauncherButton onClick={onAsk} {...kitButtonProps('secondary')} />
        </span>
      ) : null}
      {!loading && mentionsOn && bellShown && me ? <NotificationBell /> : null}
      <span className="k-wide-only">
        <ThemeToggle />
      </span>
      {loading ? <span className="k-meta">Loading…</span> : <UserMenu me={me} onAsk={askShown ? onAsk : undefined} />}
    </>
  );
}
